import { useState } from 'react';
import { chartDailySeries } from './domain';
import {
  VBX,
  VBY,
  useChartGeometry,
  linePath,
  ChartStatusLegend,
  ChartBandsTooltip,
  ChartHoverSlices,
  ChartAxes,
  ChartFrame,
  ChartNote,
} from './chartParts';
import type { Booking, ChartSeriesSpec, DateWindow, SeriesKey } from './types';

export interface ChartProps {
  // A horizontal drag pages the window — given only where paging exists (the
  // Daily page's phone-tier fortnight). Absent, a drag does nothing and only
  // the press-and-hold read is available.
  onSwipe?: (dir: 1 | -1) => void;
  bookings: Booking[];
  viewWindow: DateWindow;
  // The bands to draw — one line/stack per status or per area (group-by).
  bands: ChartSeriesSpec[];
  // Header title — reflects the active group ("…by status" / "…by area").
  title: string;
}

// Occupancy over time — one line per band (status or area). AC3 "occupancy over
// time". Computed client-side from the bookings array (Tara OQ-4). Carried
// from _prototype/bookings.jsx.
export function BookingsLineChart({ bookings, viewWindow, bands, title, onSwipe }: ChartProps) {
  const series = chartDailySeries(bookings, viewWindow.from, viewWindow.to);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  // Paging is what makes `series` shorter — the last page of a range is partial
  // — so an index taken in a fourteen-day window can outlive it. Every read
  // below asserted non-null on `series[hover]`, which is a promise the data
  // cannot keep. Deriving the point instead of indexing blind means a stale
  // index reads as "nothing hovered", which is the truth of it.
  const hover = hoverIdx != null && hoverIdx < series.length ? hoverIdx : null;

  // Empty renders INSIDE the frame, so the card keeps its height. A dashed
  // panel here made the page jump every time a range came back with no stays.
  if (series.length === 0) {
    return (
      <ChartFrame title={title} legend={<ChartStatusLegend bands={bands} />}>
        <ChartNote>No data in this range.</ChartNote>
      </ChartFrame>
    );
  }

  const { yMax, yTicks, xTicks } = useChartGeometry(series, bands);
  const xAt = (i: number) => (series.length === 1 ? VBX / 2 : (i / (series.length - 1)) * VBX);
  const yAt = (v: number) => VBY - (v / yMax) * VBY;
  const buildPath = (key: SeriesKey) => linePath(series.length, (i) => [xAt(i), yAt(series[i]![key])]);
  const leftPct = hover != null ? (hover / Math.max(1, series.length - 1)) * 100 : 0;
  // Anchor the tooltip to the topmost line at the hovered day, as a % of height.
  const anchorPct =
    hover != null
      ? Math.max(
          0,
          Math.min(100, (yAt(Math.max(...bands.map((s) => series[hover]![s.key]))) / VBY) * 100),
        )
      : 0;

  return (
    <ChartFrame title={title} legend={<ChartStatusLegend bands={bands} />}>
      <div className="chart-svg-wrap" onMouseLeave={() => setHoverIdx(null)}>
          <svg
            className="chart-svg"
            width="100%"
            height="100%"
            viewBox={`0 0 ${VBX} ${VBY}`}
            preserveAspectRatio="none"
            role="img"
            aria-label="Occupancy over time by booking status"
          >
            {yTicks.map((v, i) => (
              <line
                key={'g' + i}
                x1="0"
                y1={yAt(v)}
                x2={VBX}
                y2={yAt(v)}
                stroke="var(--border)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {bands.map((s) => (
              <path
                key={s.key}
                d={buildPath(s.key)}
                fill="none"
                stroke={s.line}
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                strokeLinejoin="round"
                strokeLinecap="round"
                style={{
                  opacity: hover == null || series[hover]![s.key] > 0 ? 1 : 0.35,
                }}
              />
            ))}
            {hover != null && (
              <line
                x1={xAt(hover)}
                y1="0"
                x2={xAt(hover)}
                y2={VBY}
                stroke="var(--ink-3)"
                strokeWidth="1"
                strokeDasharray="2 3"
                vectorEffect="non-scaling-stroke"
              />
            )}
            <ChartHoverSlices
              series={series}
              onIndex={setHoverIdx}
              onClear={() => setHoverIdx(null)}
              onSwipe={onSwipe}
            />
          </svg>
          {/* Focus dots as fixed-size HTML overlay — an SVG <circle> distorts
              under preserveAspectRatio="none" (x and y scale differently) and
              scales with the viewBox. Positioned as % of the wrap, which maps
              1:1 to the SVG coords since the svg fills it. */}
          {hover != null &&
            bands.map((s) => {
              const v = series[hover]![s.key];
              if (v <= 0) return null;
              return (
                <span
                  key={s.key}
                  className="chart-focus-dot"
                  style={{
                    left: (xAt(hover) / VBX) * 100 + '%',
                    top: (yAt(v) / VBY) * 100 + '%',
                    background: s.line,
                  }}
                />
              );
            })}
          <ChartBandsTooltip
            point={hover != null ? series[hover]! : null}
            bands={bands}
            leftPct={leftPct}
            anchorPct={anchorPct}
          />
        </div>
      <ChartAxes yTicks={yTicks} xTicks={xTicks} seriesLength={series.length} />
    </ChartFrame>
  );
}
